import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import { Organization, OrganizationRiderDetail, OrganizationRiderServiceLevel, OrganizationServiceLevel } from '@uzurv/clara';
import { isNullOrUndefined, PhoneNumberInputDirective, PhoneNumberUtil } from 'projects/sdk/packages/core/public_api';
import { MaterialModule } from 'projects/uzurv/src/app/material.module';
import { OrganizationRiderState } from 'projects/uzurv/src/store/organization-rider/organization-rider.reducer';
import * as OrganizationRiderActions from 'projects/uzurv/src/store/organization-rider/organization-rider.actions';
import { cloneDeep } from 'lodash';
import { RequiresPermissionsModule } from '../../directives';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActionTooltipModule } from '../../directives/action-tooltip';
import { StringValidators } from '@uzurv/core';
import { OrganizationRiderServiceLevelsModule } from '../../organization-rider-service-levels';
export interface EditOrganizationRiderData {
  organizations: Array<Organization>;
  organization: Organization;
  organizationRiderDetail: OrganizationRiderDetail;
  organizationServiceLevels: Array<OrganizationServiceLevel>;
}

@Component({
  selector: 'app-edit-organization-rider',
  templateUrl: './edit-organization-rider-dialog.component.html',
  styleUrls: ['./edit-organization-rider-dialog.component.scss'],
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PhoneNumberInputDirective,
    RequiresPermissionsModule,
    MatTooltipModule,
    ActionTooltipModule,
    OrganizationRiderServiceLevelsModule
  ]
})
export class EditOrganizationRiderDialogComponent implements OnInit {
  editOrganizationRiderForm: FormGroup;

  organizationRiderServiceLevels: Array<OrganizationRiderServiceLevel>;

  editedOrganizationRiderDetail: OrganizationRiderDetail;
  shouldShowDeclineTooltip: boolean;
  invalidPhoneNumber: boolean;

  constructor(
    private dialogRef: MatDialogRef<EditOrganizationRiderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditOrganizationRiderData,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private store$: Store<State<OrganizationRiderState>>
  ) {}

  get emailTooltipDescription(): string {
    return `
      Would you like to provide an email address to:
      <br>• Receive receipt emails from UZURV
      <br>• Register for our Rider App
    `;
  }

  tooltipDismissCallback = () => this.handleTooltipDismiss();

  ngOnInit(): void {
    this.editedOrganizationRiderDetail = cloneDeep(this.data.organizationRiderDetail);
    this.organizationRiderServiceLevels = cloneDeep(this.data.organizationRiderDetail.organizationRiderServiceLevels);

    this.shouldShowDeclineTooltip =
      isNullOrUndefined(this.data.organizationRiderDetail?.riderEmailDeclined) &&
      isNullOrUndefined(this.data.organizationRiderDetail.emailAddress);

    this.editOrganizationRiderForm = this.formBuilder.group({
      firstName: [this.editedOrganizationRiderDetail.firstName, Validators.required],
      lastName: [this.editedOrganizationRiderDetail.lastName, Validators.required],
      emailAddress: [this.editedOrganizationRiderDetail.emailAddress],
      phoneNumber: [this.editedOrganizationRiderDetail.phoneNumber, Validators.required],
      dateOfBirth: [
        this.editedOrganizationRiderDetail.dateOfBirth,
        isNullOrUndefined(this.editedOrganizationRiderDetail.dateOfBirth) ? null : Validators.required
      ],
      organization: [this.data.organization],
      partnerIdentifier: [this.editedOrganizationRiderDetail.partnerIdentifier, StringValidators.noSpecialCharacters()]
    });

    if (!isNullOrUndefined(this.editedOrganizationRiderDetail.phoneNumber)) {
      this.invalidPhoneNumber = !PhoneNumberUtil.isValidPhoneNumber(
        this.editedOrganizationRiderDetail.phoneNumber,
        PhoneNumberUtil.getCountryCode(this.editedOrganizationRiderDetail.phoneNumber)
      );
    }
  }

  handleSubmission() {
    this.editedOrganizationRiderDetail.firstName = this.editOrganizationRiderForm.get('firstName')?.value;
    this.editedOrganizationRiderDetail.lastName = this.editOrganizationRiderForm.get('lastName')?.value;
    this.editedOrganizationRiderDetail.emailAddress = this.editOrganizationRiderForm.get('emailAddress')?.value;
    this.editedOrganizationRiderDetail.phoneNumber = this.editOrganizationRiderForm.get('phoneNumber')?.value;
    this.editedOrganizationRiderDetail.dateOfBirth = this.editOrganizationRiderForm.get('dateOfBirth')?.value;
    this.editedOrganizationRiderDetail.partnerIdentifier = this.editOrganizationRiderForm.get('partnerIdentifier')?.value;

    this.store$.dispatch(
      OrganizationRiderActions.ModifyOrganizationRiderDetail({
        payload: {
          organizationRiderDetail: this.editedOrganizationRiderDetail,
          organizationRiderServiceLevels: isNullOrUndefined(this.organizationRiderServiceLevels)
            ? null
            : this.organizationRiderServiceLevels
        }
      })
    );
    this.dialogRef.close();
  }

  handleOrganizationServiceLevelChange(event: OrganizationRiderServiceLevel) {
    const existingOrganizationRiderServiceLevel = this.organizationRiderServiceLevels?.find(
      orls => orls.serviceLevel?.code === event.serviceLevel?.code
    );

    if (isNullOrUndefined(existingOrganizationRiderServiceLevel)) {
      this.organizationRiderServiceLevels.push(event);
    } else {
      existingOrganizationRiderServiceLevel.enabled = event.enabled;
    }
  }

  handleTooltipDismiss() {
    this.shouldShowDeclineTooltip = false;
    this.editedOrganizationRiderDetail.riderEmailDeclined = true;
  }

  get shouldShowEmailDeclineSubtext(): boolean {
    return (
      this.data.organizationRiderDetail.riderEmailDeclined && !isNullOrUndefined(this.data.organizationRiderDetail.riderEmailDeclinedAt)
    );
  }
}
